import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/common/Card";
import { Button } from "@/components/common/Button";
import { Modal } from "@/components/common/Modal";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { journalService } from "@/services/journal";
import { batchesService } from "@/services/batches";
import { JournalEntry } from "@/types";
import { exportJournalToExcel } from "@/utils/exportJournalToExcel";
import { errorMessage } from "@/utils/errorMessage";
import toast from "react-hot-toast";
import { ArrowLeft, Download, BookOpen, Unlock } from "lucide-react";
import { format } from "date-fns";

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
}

function calculateBaseAmount(
  amount: number | null | undefined,
  taxAmount: number | null | undefined,
): number {
  const lineAmount = amount ?? 0;
  if (lineAmount === 0) return 0;
  // For positive amounts: add absolute tax; for negative amounts: subtract absolute tax
  const sign = lineAmount > 0 ? 1 : -1;
  return Math.round((lineAmount + sign * Math.abs(taxAmount ?? 0)) * 100) / 100;
}

interface UnlockConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

function UnlockConfirmModal({ isOpen, onClose, onConfirm, isLoading }: UnlockConfirmModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Unlock this batch?"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} isLoading={isLoading}>
            Unlock
          </Button>
        </div>
      }
    >
      <p className="text-sm text-gray-700 dark:text-gray-300">
        Unlocking will <span className="font-semibold">not</span> reverse the original journal
        already posted to Sage. It will clear the posted date in Salesforce and remove this posting
        record, allowing the journal to be built and posted again from Salesforce&apos;s current
        data.
      </p>
      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">Do you want to continue?</p>
    </Modal>
  );
}

export function JournalSnapshotPage(): JSX.Element {
  const { batchName } = useParams<{ batchName: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isUnlockConfirmOpen, setIsUnlockConfirmOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["journalSnapshot", batchName],
    queryFn: () => journalService.getPostingSnapshot(batchName!),
    enabled: !!batchName,
  });

  const snapshot = data?.data;

  const unlockMutation = useMutation({
    mutationFn: () => batchesService.unlockBatch(batchName!),
    onSuccess: () => {
      toast.success("Batch unlocked. It can now be posted again.");
      queryClient.invalidateQueries({ queryKey: ["batches"] });
      queryClient.removeQueries({ queryKey: ["journalSnapshot", batchName] });
      setIsUnlockConfirmOpen(false);
      navigate("/batches");
    },
    onError: (err: unknown) => {
      toast.error(errorMessage(err, "Failed to unlock batch"));
    },
  });

  const handleExport = async () => {
    if (!snapshot) return;

    const totalsForExport = {
      totalDebit: snapshot.totals.totalDebit,
      totalCredit: snapshot.totals.totalCredit,
      totalBaseDebit: snapshot.entries.reduce(
        (sum, e) => sum + calculateBaseAmount(e.debit, e.taxAmount),
        0,
      ),
      totalBaseCredit: snapshot.entries.reduce(
        (sum, e) => sum + calculateBaseAmount(e.credit, e.taxAmount),
        0,
      ),
      totalTaxAmount: snapshot.entries.reduce((sum, e) => sum + Math.abs(e.taxAmount ?? 0), 0),
      bankTotal: snapshot.totals.bankTotal,
      revenueTotal: snapshot.totals.revenueTotal,
      balance: snapshot.totals.balance,
    };

    await exportJournalToExcel({
      entries: snapshot.entries,
      totals: totalsForExport,
      batchName: snapshot.batchName,
      journal: snapshot.journalId,
      postDate: snapshot.postDate,
      description: snapshot.description,
      location: snapshot.location,
    });
    toast.success("Journal exported to Excel");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !snapshot) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate("/batches")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              View Posted Journal
            </h1>
          </div>
          {batchName && (
            <Button variant="danger" onClick={() => setIsUnlockConfirmOpen(true)}>
              <Unlock className="h-4 w-4 mr-2" />
              Unlock
            </Button>
          )}
        </div>
        <Card>
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No posting snapshot available</p>
            <p className="text-sm mt-1">
              This batch was posted before snapshot recording was enabled.
            </p>
          </div>
        </Card>

        <UnlockConfirmModal
          isOpen={isUnlockConfirmOpen}
          onClose={() => setIsUnlockConfirmOpen(false)}
          onConfirm={() => unlockMutation.mutate()}
          isLoading={unlockMutation.isPending}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[90vh] overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate("/batches")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                View Posted Journal
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Posted on {format(new Date(snapshot.postedAt), "MMM d, yyyy h:mm a")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="danger" onClick={() => setIsUnlockConfirmOpen(true)}>
              <Unlock className="h-4 w-4 mr-2" />
              Unlock
            </Button>
            <Button variant="secondary" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export to Excel
            </Button>
          </div>
        </div>

        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
          Batch ID: {snapshot.batchName}
        </h4>

        {/* Header metadata */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">Journal:</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {snapshot.journalId}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">Post Date:</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {snapshot.postDate}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">Description:</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {snapshot.description}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">Location:</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {snapshot.location}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">Batch Amount:</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatCurrency(snapshot.batchAmount)}
            </span>
          </div>
        </div>
      </div>

      {/* Data Grid */}
      <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md flex flex-col h-full">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Journal Entries ({snapshot.entries.length}) — {snapshot.opportunityCount}{" "}
              opportunities
            </h3>
          </div>

          {snapshot.entries.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
              <div className="text-center py-8">
                <p>No journal entries were posted for this batch.</p>
                <p className="text-sm mt-1">Only the Salesforce posted date was updated.</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-auto pb-4 min-h-0">
              <table
                className="text-sm"
                style={{
                  tableLayout: "fixed",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  minWidth: "max-content",
                }}
              >
                <thead className="sticky top-0" style={{ zIndex: 10 }}>
                  <tr>
                    {[
                      { key: "row", label: "Row", width: 60 },
                      { key: "journalType", label: "Journal Type", width: 128 },
                      { key: "memo", label: "Memo", width: 280 },
                      { key: "accountNumber", label: "Account #", width: 180 },
                      { key: "departmentId", label: "Department ID", width: 180 },
                      { key: "projectId", label: "Project ID", width: 180 },
                      { key: "classId", label: "Class ID", width: 180 },
                      { key: "pfpCode", label: "PFP Code", width: 160 },
                      { key: "contributionCode", label: "Contribution Code", width: 200 },
                      { key: "debit", label: "Debit", width: 160 },
                      { key: "credit", label: "Credit", width: 160 },
                      { key: "baseDebit", label: "Base Debit", width: 160 },
                      { key: "baseCredit", label: "Base Credit", width: 160 },
                      { key: "taxId", label: "Tax ID", width: 240 },
                      { key: "taxAmount", label: "Tax Amount", width: 160 },
                    ].map((col) => (
                      <th
                        key={col.key}
                        className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-left select-none"
                        style={{ width: col.width, minWidth: col.width }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {snapshot.entries.map((entry: JournalEntry, index: number) => {
                    const baseDebit = calculateBaseAmount(entry.debit, entry.taxAmount);
                    const baseCredit = calculateBaseAmount(entry.credit, entry.taxAmount);

                    return (
                      <tr
                        key={entry.id || index}
                        className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                      >
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{index + 1}</td>
                        <td
                          className="px-3 py-2 text-gray-900 dark:text-gray-100 truncate"
                          style={{ maxWidth: 128 }}
                        >
                          {entry.journalType}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-900 dark:text-gray-100 truncate"
                          style={{ maxWidth: 280 }}
                          title={entry.memo}
                        >
                          {entry.memo}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-900 dark:text-gray-100 truncate"
                          style={{ maxWidth: 180 }}
                        >
                          {entry.accountNumber}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-900 dark:text-gray-100 truncate"
                          style={{ maxWidth: 180 }}
                        >
                          {entry.departmentId}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-900 dark:text-gray-100 truncate"
                          style={{ maxWidth: 180 }}
                        >
                          {entry.projectId}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-900 dark:text-gray-100 truncate"
                          style={{ maxWidth: 180 }}
                        >
                          {entry.classId}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-900 dark:text-gray-100 truncate"
                          style={{ maxWidth: 160 }}
                        >
                          {entry.pfpCode}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-900 dark:text-gray-100 truncate"
                          style={{ maxWidth: 200 }}
                        >
                          {entry.contributionCode}
                        </td>
                        <td
                          className="px-3 py-2 text-right text-gray-900 dark:text-gray-100"
                          style={{ width: 160 }}
                        >
                          {entry.debit != null && entry.debit !== 0
                            ? formatCurrency(entry.debit)
                            : ""}
                        </td>
                        <td
                          className="px-3 py-2 text-right text-gray-900 dark:text-gray-100"
                          style={{ width: 160 }}
                        >
                          {entry.credit != null && entry.credit !== 0
                            ? formatCurrency(entry.credit)
                            : ""}
                        </td>
                        <td
                          className="px-3 py-2 text-right text-gray-900 dark:text-gray-100"
                          style={{ width: 160 }}
                        >
                          {baseDebit > 0 ? formatCurrency(baseDebit) : ""}
                        </td>
                        <td
                          className="px-3 py-2 text-right text-gray-900 dark:text-gray-100"
                          style={{ width: 160 }}
                        >
                          {baseCredit > 0 ? formatCurrency(baseCredit) : ""}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-900 dark:text-gray-100 truncate"
                          style={{ maxWidth: 240 }}
                        >
                          {entry.taxId}
                        </td>
                        <td
                          className="px-3 py-2 text-right text-gray-900 dark:text-gray-100"
                          style={{ width: 160 }}
                        >
                          {entry.taxAmount != null && entry.taxAmount !== 0
                            ? formatCurrency(Math.abs(entry.taxAmount))
                            : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="sticky bottom-0 bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600">
                  <tr className="font-semibold text-gray-900 dark:text-gray-100">
                    <td
                      colSpan={9}
                      className="px-3 py-2 text-right text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400"
                    >
                      Totals
                    </td>
                    <td className="px-3 py-2 text-right">
                      {formatCurrency(snapshot.totals.totalDebit)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {formatCurrency(snapshot.totals.totalCredit)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {formatCurrency(
                        snapshot.entries.reduce(
                          (s, e) => s + calculateBaseAmount(e.debit, e.taxAmount),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {formatCurrency(
                        snapshot.entries.reduce(
                          (s, e) => s + calculateBaseAmount(e.credit, e.taxAmount),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2 text-right">
                      {formatCurrency(
                        snapshot.entries.reduce((s, e) => s + Math.abs(e.taxAmount ?? 0), 0),
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Footer summary */}
          <div className="flex-shrink-0 px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex gap-6 text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Bank Total:{" "}
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(snapshot.totals.bankTotal)}
                </span>
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                Revenue Total:{" "}
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(snapshot.totals.revenueTotal)}
                </span>
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                Balance:{" "}
                <span
                  className={`font-semibold ${
                    Math.abs(snapshot.totals.balance) < 0.01
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {formatCurrency(snapshot.totals.balance)}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <UnlockConfirmModal
        isOpen={isUnlockConfirmOpen}
        onClose={() => setIsUnlockConfirmOpen(false)}
        onConfirm={() => unlockMutation.mutate()}
        isLoading={unlockMutation.isPending}
      />
    </div>
  );
}
